import { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, fetchUsdRates, DEFAULT_RATES, convertCurrency, getCurrencySymbol } from '../utils/currency';

// Provides currency helpers (formatter + converter) backed by cached USD rates.
export default function useCurrencyTools() {
  const { currencyCode } = useAuth();
  const preferredCurrency = currencyCode || 'INR';
  const [rates, setRates] = useState(DEFAULT_RATES);
  const [loadingRates, setLoadingRates] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoadingRates(true);
      try {
        const latest = await fetchUsdRates();
        if (mounted && latest) setRates(latest);
      } catch (err) {
        console.warn('Rate fetch failed, using defaults', err);
      } finally {
        if (mounted) setLoadingRates(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  const formatMoney = useCallback((amount, code = preferredCurrency, opts = {}) =>
    formatCurrency(amount, code, opts), [preferredCurrency]);

  const convertMoney = useCallback((amount, fromCode, toCode = preferredCurrency) =>
    convertCurrency(amount, fromCode, toCode, rates), [preferredCurrency, rates]);

  const convertAndFormat = useCallback((amount, fromCode, toCode = preferredCurrency, opts = {}) =>
    formatMoney(convertMoney(amount, fromCode, toCode), toCode, opts), [preferredCurrency, formatMoney, convertMoney]);

  const value = useMemo(() => ({
    preferredCurrency,
    rates,
    loadingRates,
    formatMoney,
    convertMoney,
    convertAndFormat,
    currencySymbol: getCurrencySymbol(preferredCurrency),
  }), [preferredCurrency, rates, loadingRates, formatMoney, convertMoney, convertAndFormat]);

  return value;
}
